<template>
    <div class="lg:hidden bg-zinc-900 px-4 pt-6 pb-5 border-b border-white/10">
        <!-- View: All -->
        <template v-if="view === 'all'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                Your Stars Unlock Fantasies
            </h1>

            <!-- Guest Content -->
            <template v-if="!isUserLoggedIn">
                <p class="mt-2 text-base text-white/70 leading-relaxed">
                    Members sponsor auditions to move performers toward production. When an audition is fully sponsored, it becomes an official release.
                </p>

                <div class="mt-4">
                    <button
                        @click="$emit('joinClick')"
                        class="w-full h-11 rounded-2xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold transition-all shadow-lg shadow-blue-600/25"
                    >
                        Get Stars to Sponsor
                    </button>
                </div>
            </template>

            <!-- Logged In Content -->
            <template v-else>
                <p class="mt-2 text-base text-white/70 leading-relaxed">
                    Every audition you support moves a performer closer to production. Fully sponsored auditions are produced as official releases.
                </p>
            </template>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <div class="flex items-center gap-1.5">
                    <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span>
                        <span class="text-white/90 font-semibold">
                          {{ formatStars(totalStarsGiven) }}
                        </span>
                        stars given
                    </span>
                </div>

                <div class="h-3 w-px bg-white/10" />

                <div class="flex items-center gap-1.5">
                    <TrendingUp class="w-3.5 h-3.5 text-white/40" />
                    <span>
                        <span class="text-white/90 font-semibold">{{ sponsoredLast30Days }}</span> sponsored last 30 days
                    </span>
                </div>
            </div>

            <!-- Top Sponsors - Inline on Mobile -->
            <div class="mt-3 flex items-center gap-2">
                <Trophy class="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                <span class="text-white/70 text-xs font-semibold whitespace-nowrap">
                    Top Sponsors:
                </span>

                <div
                    class="overflow-x-auto flex-1"
                    :style="{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        WebkitOverflowScrolling: 'touch'
                    }"
                >
                    <div class="flex gap-2 pb-2">
                        <button
                            v-for="(sponsor, index) in topSponsors.slice(0, maxVisibleTopSponsors)"
                            :key="sponsor.user_id"
                            @click="$emit('openLeaderboard')"
                            class="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 hover:bg-white/[0.08] active:bg-white/[0.1] transition"
                        >
                            <span class="text-white/50 text-xs font-medium">#{{ sponsor.rank }}</span>
                            <span class="text-white text-xs font-semibold whitespace-nowrap">{{ sponsor.sponsor_name }}</span>
                            <span class="text-white/70 text-xs font-semibold">{{ sponsor.total_stars }}</span>
                            <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3 h-3" />
                        </button>

                        <!-- View All pill at end -->
                        <button
                            @click="$emit('openLeaderboard')"
                            class="flex-shrink-0 px-4 py-1.5 rounded-full border border-white/20 text-white/70 hover:text-white text-xs font-semibold whitespace-nowrap transition"
                        >
                            View All →
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <!-- View: Mine -->
        <template v-if="view === 'mine'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                My Sponsorships
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Track what you've sponsored and see which auditions are closest to
                being released.
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <span>
                    <span class="text-white/90 font-semibold">
                        {{ userSponsoredCount }}
                    </span>
                    auditions sponsored
                </span>
                <div class="h-3 w-px bg-white/10" />
                <span>
                    <span class="text-white/90 font-semibold">
                        {{ userTotalStarsGiven }}
                    </span>
                    stars given
                </span>
            </div>
        </template>

        <!-- View: Sponsored -->
        <template v-if="view === 'sponsored'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                Community-Backed Auditions
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Auditions that have received star support from members, working their way toward a full production.
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <span>
                    <span class="text-white/90 font-semibold">{{ totalSponsored }}</span>
                    auditions sponsored
                </span>
                <div class="h-3 w-px bg-white/10" />
                <div class="flex items-center gap-1.5">
                    <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span>
                        <span class="text-white/90 font-semibold">{{ formatStars(sponsoredTotalStarsGiven) }}</span> stars given
                    </span>
                </div>
            </div>
        </template>

        <!-- View: Released -->
        <template v-if="view === 'released'">
            <h1 class="text-3xl font-bold text-white tracking-tight">
                Sponsored & Released
            </h1>
            <p class="mt-2 text-base text-white/65 leading-relaxed">
                Watch the released scenes made possible by community sponsorship.
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/50">
                <span>
                    <span class="text-white/90 font-semibold">
                        {{ releasedCount }}
                    </span>
                    scenes released
                </span>
                <div class="h-3 w-px bg-white/10" />
                <span>
                    <span class="text-white/90 font-semibold">{{ sponsoredLast30Days }}</span> sponsored last 30 days
                </span>
            </div>
        </template>
    </div>
</template>

<script setup>
import { TrendingUp, Trophy } from 'lucide-vue-next'
import {ref} from "vue";

const props = defineProps({
    view: {
        type: String,
        required: true,
        validator: (value) => ['all', 'mine', 'released', 'sponsored'].includes(value)
    },
    isUserLoggedIn: {
        type: Boolean,
        required: true
    },
    sponsoredThisMonth: {
        type: Number,
        required: true
    },
    sponsoredLast30Days: {
        type: Number,
        required: true
    },
    totalSponsored: {
        type: Number,
        required: true
    },
    sponsoredTotalStarsGiven: {
        type: Number,
        required: true
    },
    totalStarsGiven: {
        type: Number,
        required: true
    },
    userSponsoredCount: {
        type: Number,
        required: true
    },
    releasedCount: {
        type: Number,
        required: true
    },
    userTotalStarsGiven: {
        type: Number,
        required: true
    },
    topSponsors: {
        type: Array,
        default: []
    },
})

defineEmits(['openLeaderboard', 'joinClick'])

const maxVisibleTopSponsors = ref(10)

const formatStars = (num) => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M+`;
    }
    if (num >= 100000) {
        return `${Math.floor(num / 1000)}K+`;
    }
    return num.toLocaleString()
}
</script>
